import {useCallback} from 'react';
import {useSelector, useDispatch} from 'react-redux';

import {Button} from 'shells/button/button';
import {Stack} from 'shells/layout/stack/stack';

import {addNotification} from '../../../notifications/index';

import {TAB_ACTIVITY, isFormTabSelected} from '../redux/consts';
import {setActionBarTab, setCreatorFormActive} from '../redux/core-entity-page-reducer';
import {getCoreEntityPageCreatorForm} from '../redux/core-entity-page-selectors';
import type {TabOption} from '../redux/types';
import type {ActionBarOption} from '../workspace/action-bar';
import {CreateTimelineEventForm} from '../workspace/create-timeline-event-form';
import {getTimelineItemNotificationArgs} from '../workspace/helpers';

import styles from './mini-action-bar.module.css';

interface MiniActionBarProps {
    entityId: string;
    entityName: string;
    entityDisplayName: string;
    options: ActionBarOption[];
    selectedTab: TabOption;
    isShown: boolean;
    onAfterSubmit?: () => void;
}

export function MiniActionBar(props: MiniActionBarProps) {
    const {entityId, entityName, entityDisplayName, options, selectedTab, isShown, onAfterSubmit} =
        props;
    const dispatch = useDispatch();

    const {isActive: isCreatorFormActive, notePlaintextValue} = useSelector(
        getCoreEntityPageCreatorForm
    );

    const isFormOpen = isFormTabSelected(selectedTab) && isCreatorFormActive;

    const handleClick = (option: ActionBarOption) => {
        if (isFormTabSelected(option.value)) {
            dispatch(setActionBarTab(option.value));
            dispatch(setCreatorFormActive(true));
        } else if (option.onClick) {
            option.onClick();
        }
    };

    const handleFormClose = useCallback(() => {
        dispatch(setCreatorFormActive(false));
    }, [dispatch]);

    const handleFormBlur = useCallback(() => {
        if (notePlaintextValue.trim() === '') {
            dispatch(setCreatorFormActive(false));
        }
    }, [dispatch, notePlaintextValue]);

    const handleAfterSubmit = useCallback(
        (id: string, activityTypeName?: string, noteBody?: string) => {
            dispatch(setCreatorFormActive(false));
            onAfterSubmit?.();
            const contentType = selectedTab === TAB_ACTIVITY ? 'ACTIVITY' : 'NOTE';
            const {title, options} = getTimelineItemNotificationArgs(
                contentType,
                id,
                entityDisplayName,
                activityTypeName,
                noteBody
            );
            addNotification(title, options);
        },
        [onAfterSubmit, dispatch, selectedTab, entityDisplayName]
    );

    return (
        <Stack spacing={8} isFullWidth={true}>
            <div className='background-white flex gap-8'>
                {options.map((option) => (
                    <Button
                        key={option.value}
                        id={`${option.value}-fixed`}
                        onClick={() => handleClick(option)}
                        variant='secondary'
                        size='big'
                        icon={option.icon}
                    >
                        {option.title || ''}
                    </Button>
                ))}
            </div>
            {isFormOpen && isShown && (
                <div className={styles['form-container']}>
                    <CreateTimelineEventForm
                        entityId={entityId}
                        entityName={entityName}
                        contentType={selectedTab === TAB_ACTIVITY ? 'ACTIVITY' : 'NOTE'}
                        showPlaceholder={false}
                        isOpen={isFormOpen}
                        onOpenChange={(open) => {
                            if (!open) {
                                handleFormClose();
                            }
                        }}
                        onAfterSubmit={handleAfterSubmit}
                        onBlur={handleFormBlur}
                        onFocus={() => dispatch(setCreatorFormActive(true))}
                    />
                </div>
            )}
        </Stack>
    );
}
